import * as React from 'react'
import { scaleLinear } from '@vx/scale'
import { Group } from '@vx/group'
import { HeatmapRect } from '@vx/heatmap'
import { genBins } from '@vx/mock-data'
import { min, max } from 'd3-array'
import { withTooltip } from '@vx/tooltip'
import { AxisRight, AxisBottom } from '@vx/axis'
import { localPoint } from '@vx/event'
import { GraphTooltip } from '../GraphTooltip'
import LinedTable from 'components/Tables/LinedTable'
import { round, unionBy } from 'lodash'
import { merge } from 'icepick'
import * as colors from 'styles/colors-export.css'

const numDays = 7
const numHours = 24
const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

// accessors
const x = d => d.bin
const y = d => d.bins
const z = d => d.count

const baseData = genBins(
  numDays,
  numHours,
  i => i,
  () => 0
)

export interface IGraphBin {
  bin: string | number
  bins: {
    bin: string | number
    count: number
  }[]
}

export interface IGraphHeatmapProps {
  width: number
  height: number
  data: IGraphBin[]
}

const mergeResolover = (targetVal, sourceVal, key) => {
  if (Array.isArray(targetVal) && sourceVal) {
    return unionBy(sourceVal, targetVal, 'bin')
  } else {
    return sourceVal
  }
}

export class GraphHeatmapComponent extends React.Component<any> {
  render() {
    const {
      data,
      width,
      height,
      tooltipData,
      tooltipLeft,
      tooltipTop,
      tooltipOpen,
      hideTooltip,
      showTooltip,
      margin = {
        top: 0,
        left: 0,
        right: 30,
        bottom: 35,
      },
    } = this.props

    const dataMerged = merge(baseData, data, mergeResolover)

    if (width < 100) {
      return null
    }

    // bounds
    const xMax = width - margin.left - margin.right
    const yMax = height - margin.top - margin.bottom
    const dMin = min(dataMerged, d => min(y(d), x))
    const dMax = max(dataMerged, d => max(y(d), x))
    const bWidth = xMax / numDays
    const bHeight = yMax / numHours
    const colorMax = max(dataMerged, d => max(y(d), z))

    // scales
    const xScale = scaleLinear({
      range: [0, xMax],
      domain: [0, numDays],
    })
    const yScale = scaleLinear({
      range: [0, yMax],
      domain: [dMin, dMax],
    })
    const colorScale = scaleLinear({
      range: ['lightgray', colors.tertiary],
      domain: [0.8, colorMax],
    })
    const opacityScale = scaleLinear({
      range: [0.3, 1],
      domain: [0, colorMax],
    })

    return (
      <div>
        <svg width={width} height={height}>
          <Group top={-margin.bottom}>
            <HeatmapRect
              data={dataMerged}
              xScale={xScale}
              yScale={yScale}
              colorScale={colorScale}
              opacityScale={opacityScale}
              binWidth={bWidth}
              binHeight={bHeight}
              step={2}
              gap={2}
              onMouseOut={() => () => hideTooltip()}
              onMouseOver={data => event => {
                const coords = localPoint(event.target.ownerSVGElement, event)
                showTooltip({
                  tooltipLeft: coords.x,
                  tooltipTop: coords.y,
                  tooltipData: data.bin,
                })
              }}
            />
          </Group>
          <AxisBottom
            hideAxisLine
            hideTicks
            scale={xScale}
            tickFormat={value => days[value] && days[value].substring(0, 3)}
            numTicks={6}
            top={height - 25}
            left={bWidth / 2}
          />
          <AxisRight
            hideAxisLine
            hideTicks
            scale={yScale}
            numTicks={numHours / 2}
            left={xMax}
            top={bHeight / 2 + 2}
          />
        </svg>
        {tooltipOpen && (
          <GraphTooltip top={tooltipTop} left={tooltipLeft}>
            <LinedTable>
              {tooltipData.day !== undefined && (
                <div>
                  <div>Day:</div>
                  <div>{days[tooltipData.day]}</div>
                </div>
              )}
              {tooltipData.hour !== undefined && (
                <div>
                  <div>Hour:</div>
                  <div>
                    {tooltipData.hour}:00 - {tooltipData.hour}:59
                  </div>
                </div>
              )}
              <div>
                <div>Calls:</div>
                <div>{round(tooltipData.count)}</div>
              </div>
            </LinedTable>
          </GraphTooltip>
        )}
      </div>
    )
  }
}

export const GraphHeatmap = withTooltip(GraphHeatmapComponent) as React.SFC<IGraphHeatmapProps>



// WEBPACK FOOTER //
// src/features/CallAnalytics/GraphHeatmap/GraphHeatmap.tsx